<script setup lang="ts">
import { ref, useTemplateRef, onMounted, computed, watch, shallowRef } from 'vue';
import formatDate from '@/lib/format-date';
import { useElementSize } from '@vueuse/core';
import { DashboardWidget, AdminWidget } from '@/interfaces/Dashboard';
import { InputType } from '@/interfaces/InputFields';
import { ApiResponse } from '@/interfaces/Dashboard';
import { ButtonAction } from '@/components/Atoms/ConnxButton.vue';
import useDashboardStore from '@/stores/Dashboard/dashboard-store';
import ConnxField from '@/components/Molecules/Field/ConnxField.vue';
import ConnxButton from '@/components/Atoms/ConnxButton.vue';
import ConnxIcon from '@/components/Atoms/ConnxIcon.vue';
import ConnxEntityStatusSummary from './Widgets/ConnxEntityStatusSummary.vue';
import ConnxGenericWidgetTable from '@/views/Welcome/Dashboard/Widgets/ConnxGenericWidgetTable.vue';
import ConnxGenericApexChart from '@/views/Welcome/Dashboard/Widgets/ConnxGenericApexChart.vue';
import ConnxHighTurnoverWidget from './Widgets/ConnxHighTurnoverWidget.vue';
import ConnxDataSpotlightChart from './Widgets/ConnxDataSpotlightChart.vue';
import ConnxExpirySummary from './Widgets/ConnxExpirySummary.vue';
import ConnxBulletScoreListChart from './Widgets/ConnxBulletScoreListChart.vue';
import ConnxAdvancedColumnChart from '@/views/Welcome/Dashboard/Widgets/ConnxAdvancedColumnChart.vue';
import ConnxComplianceRiskSummaryChart from './Widgets/ConnxComplianceRiskSummaryChart.vue';
import ConnxEarlyExitRateChart from '@/views/Welcome/Dashboard/Widgets/ConnxEarlyExitRateChart.vue';
import ConnxComplianceWatchStatusChart from '@/views/Welcome/Dashboard/Widgets/ConnxComplianceWatchStatusChart.vue';
import ConnxGenericStatusCard from '@/views/Welcome/Dashboard/Widgets/ConnxGenericStatusCard.vue';
import ConnxGenericBreakdownChart from '@/views/Welcome/Dashboard/Widgets/ConnxGenericBreakdownChart.vue';
import { chartIconMap } from '@/lib/chart-helpers';

const props = defineProps<{
  editing?: boolean;
  cols: number;
  rowHeight: number;
  dashboardWidgetId?: string;
  libraryWidget?: AdminWidget;
  settingsOpen: boolean;
}>();

const dashboardStore = useDashboardStore();

const widget = computed(() => {
  if (props.libraryWidget) {
    const defaultWidget = props.libraryWidget;
    return {
      ...defaultWidget,
      widgetGuid: defaultWidget.guid,
      dashboardTabWidgetGuid: defaultWidget.guid,
      pendingRemoval: false,
      defaultJsonProperties: {
        ...defaultWidget.defaultJsonProperties,
        title: defaultWidget.widgetName,
        subtitle: defaultWidget.subtitle,
        currentDisplayType: defaultWidget.defaultVisualType,
      },
    };
  } else if (props.dashboardWidgetId) {
    return dashboardStore.getDashboardWidgetById(props.dashboardWidgetId);
  }
  return null;
});
const chartContainer = useTemplateRef('chartContainer');
const { height, width } = useElementSize(chartContainer);
const chartTypeToRender = ref(widget.value?.defaultJsonProperties.currentDisplayType);
const widgetToRender = ref(widget.value?.defaultJsonProperties.componentType);
const componentRef = useTemplateRef('componentRef');
const tableData = ref<any>(null);
const optionsOpen = ref(false);

const EmptyWidget = {
  template: '<div></div>',
};

const visualDisplayComponents = new Map<string, any>([
  ['Table', ConnxGenericWidgetTable],
  ['HighTurnoverRiskAreas', ConnxHighTurnoverWidget],
  ['key-position-succession-gaps', ConnxEntityStatusSummary],
  ['EntityStatusSummaryChart', ConnxEntityStatusSummary],
  ['ExpirySummary', ConnxExpirySummary],
  ['ComplianceRiskSummaryChart', ConnxComplianceRiskSummaryChart],
  ['DataSpotlightChart', ConnxDataSpotlightChart],
  ['BulletScoreListChart', ConnxBulletScoreListChart],
  ['Title', EmptyWidget],
  ['AdvancedColumnChart', ConnxAdvancedColumnChart],
  ['Default', ConnxGenericApexChart],
  ['EarlyExitRateChart', ConnxEarlyExitRateChart],
  ['ConnxComplianceWatchStatusChart', ConnxComplianceWatchStatusChart],
  ['ConnxGenericStatusCard', ConnxGenericStatusCard],
  ['GenericBreakdownChart', ConnxGenericBreakdownChart],
]);

const widgetComponent = shallowRef();

const reRender = () => {
  const comp = componentRef.value as any;
  if (comp && typeof comp.reRender === 'function') {
    comp.reRender();
  }
};

const formatData = () => {
  const comp = componentRef.value as any;
  if (comp && typeof comp.formatData === 'function') {
    comp.formatData();
  }
};

const hasFilters = (widget: DashboardWidget) => {
  if (widget.defaultJsonProperties.parameters) {
    if (
      widget.defaultJsonProperties.parameters.filterBy &&
      widget.defaultJsonProperties.parameters.filterBy?.length > 0
    ) {
      return true;
    }
    if (widget.defaultJsonProperties.parameters.groupBy) {
      return true;
    }
    if (widget.defaultJsonProperties.parameters.orderBy) {
      return true;
    }
    if (widget.defaultJsonProperties.parameters.results) {
      return true;
    }
  }

  return false;
};

const dataUpdated = (data: ApiResponse) => {
  tableData.value = data;
};

const reportingPeriod = computed<boolean | string>(() => {
  const parameters = widget.value?.defaultJsonProperties.parameters;
  if (parameters) {
    if (parameters.previousRange && parameters.previousRange.selected) {
      let from = '';
      let to = '';
      if (parameters.previousRange.value[0]) {
        from = formatDate(parameters.previousRange.value[0], false, 'D MMM YYYY');
      }
      if (parameters.previousRange.value[1]) {
        to = formatDate(parameters.previousRange.value[1], false, 'D MMM YYYY');
      }

      if (from !== '' && to !== '') {
        return `${from} - ${to}`;
      }
      if (from !== '') {
        return `From ${from}`;
      }
    }
    if (parameters.previousMonths) {
      const { labelPrefix, value, labelAffix } = parameters.previousMonths;
      return `${labelPrefix} ${value} ${labelAffix}`;
    }
  }
  return false;
});

const actions = computed<ButtonAction[]>(() => {
  const buttonActions: ButtonAction[] = [];

  if (!widget.value) return [];
  const properties = widget.value.defaultJsonProperties;

  if (!props.editing && tableData.value) {
    let icon = properties.displayedAsTable ? chartIconMap.get(properties.currentDisplayType!)?.icon : 'fal fa-table';
    buttonActions.push({
      icon,
      text: properties.displayedAsTable ? `Chart View` : 'Table View',
      action: () => (properties.displayedAsTable = !properties.displayedAsTable),
    });
  }

  if (!props.editing && hasFilters(widget.value)) {
    buttonActions.push({
      icon: 'fal fa-filter',
      text: 'Filters',
      action: () => emit('open-settings'),
    });
  }

  if (!props.editing && widget.value.helpFieldText != null && widget.value.helpFieldText !== '') {
    buttonActions.push({
      icon: 'far fa-question-circle',
      text: 'Help',
      action: () => emit('open-help'),
    });
  }

  if (props.editing) {
    buttonActions.push({
      icon: 'fal fa-cog',
      text: 'Settings',
      action: () => emit('open-settings'),
    });
  }

  return buttonActions;
});

onMounted(() => {
  widgetComponent.value = visualDisplayComponents.get(widgetToRender.value!) || visualDisplayComponents.get('Default');
});

watch(
  () => widget.value?.defaultJsonProperties.currentDisplayType,
  async (val: string) => {
    chartTypeToRender.value = val;
    formatData();
  }
);

watch(
  () => widget.value?.defaultJsonProperties.componentType,
  async (val: string) => {
    widgetToRender.value = val;
    formatData();
  }
);

watch(optionsOpen, (val) => {
  emit('options-open', val);
});

const emit = defineEmits([
  'removeItem',
  'open-settings',
  'open-tabular-data',
  'component-type-update',
  'open-help',
  'options-open',
]);
defineExpose({ reRender });
</script>
<template>
  <div class="tw-p-4 tw-h-full tw-w-full" v-if="widget">
    <div
      class="tw-relative tw-group tw-rounded-md tw-h-full tw-px-8 tw-py-5"
      :class="{
        'tw-bg-connx-white tw-shadow-lg': widget.defaultJsonProperties.componentType !== 'Title',
        'tw-ring-2 tw-ring-connx-primary': settingsOpen,
      }"
    >
      <div class="tw-flex tw-flex-col tw-h-full">
        <div class="tw-absolute tw-top-3 tw-right-2 tw-z-1200">
          <div class="tw-text-connx-grey-tint-05">
            <div class="tw-flex tw-gap-x-4">
              <div
                class="group-hover:tw-opacity-100 tw-text-connx-grey-shade-20"
                :class="{
                  'tw-opacity-0': !optionsOpen,
                }"
              >
                <connx-button
                  v-if="widget.defaultJsonProperties.componentType !== 'Title' && actions.length > 0"
                  type="button"
                  button-style="blank"
                  dropdown-class="tw-py-2 tw-text-connx-grey-shade-20"
                  dropdown-position="bottom-left"
                  @open="(isOpen: boolean) => optionsOpen = isOpen"
                  :actions="actions"
                >
                  <template #drop-down-icon>
                    <connx-icon name="far fa-ellipsis-h" class="tw-h-5"></connx-icon>
                  </template>
                </connx-button>
              </div>
              <button
                v-if="editing"
                type="button"
                @click="emit('removeItem')"
                class="tw-bg-connx-grey-tint-75 tw-outline-none tw-border-none tw-rounded-full tw-w-8 tw-h-8 tw-flex tw-justify-center tw-items-center tw-text-connx-grey-shade-20"
              >
                <connx-icon name="fal fa-times"></connx-icon>
              </button>
            </div>
          </div>
        </div>
        <div
          v-if="widget.defaultJsonProperties.componentType === 'Title'"
          class="tw-flex tw-items-center tw-5 tw-h-full"
        >
          <connx-field
            :type="InputType.Text"
            v-model="widget.defaultJsonProperties.title"
            name="model"
            class="tw-mb-0"
            placeholder="Enter Title"
            v-if="editing"
          ></connx-field>
          <h2 class="tw-mb-0" v-else> {{ widget.defaultJsonProperties.title }}</h2>
        </div>
        <div v-else>
          <div
            v-if="widget.defaultJsonProperties.title"
            class="tw-flex tw-items-center tw-mb-4"
            :class="widget.defaultJsonProperties.title ? 'tw-justify-between' : 'tw-justify-end'"
          >
            <h4 class="tw-mb-0 tw-font-medium tw-break-word"> {{ widget.defaultJsonProperties.title }}</h4>
          </div>
        </div>
        <div class="tw-flex tw-flex-wrap tw-justify-between tw-items-start tw-gap-x-4">
          <div class="tw-flex-1 min-w-0">
            <p class="tw-break-words" v-if="widget.defaultJsonProperties.subtitle">
              {{ widget.defaultJsonProperties.subtitle }}
            </p>
          </div>
          <div v-if="reportingPeriod">
            <div class="tw-px-4 tw-py-1 tw-rounded-full tw-bg-connx-grey-tint-85">
              {{ reportingPeriod }}
            </div>
          </div>
        </div>
        <div
          class="tw-grow tw-min-h-0 tw-overflow-y-auto"
          ref="chartContainer"
          :data-name="widgetToRender"
          v-if="widgetComponent && !widget.defaultJsonProperties.displayedAsTable"
        >
          <component
            ref="componentRef"
            :is="widgetComponent"
            :height="Math.floor(height - 15)"
            :width="Math.floor(width - 15)"
            :widget="widget"
            :type="chartTypeToRender"
            @data-updated="dataUpdated"
          />
        </div>
        <div
          v-else-if="widget.defaultJsonProperties.componentType !== 'Title'"
          class="tw-h-full tw-min-h-0 tw-overflow-y-auto"
        >
          <connx-generic-widget-table :height="height" :widget="widget" :raw-data="tableData" />
        </div>
      </div>
    </div>
  </div>
</template>
